import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeProvider";
import { useMobileOptimized } from "@/hooks/useMobileOptimized";
import { motion } from "framer-motion";
import {
  BookOpen,
  Code,
  Eye,
  Layers,
  Paintbrush,
  Search,
  Share2,
  Users,
} from "lucide-react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

// Category 1: Content Creation
const contentCreationFeatures = [
  {
    id: "canvas",
    title: "All-in-One Canvas",
    description:
      "One workspace for everything you create. Code with syntax highlighting and live execution. Write with rich text and markdown support. Build spreadsheets with formulas and data tools. Design HTML with instant preview. Artifact's canvas adapts to your content type while AI assists at every step.",
    icon: Code,
    imageDark: "/assets/features/intelligent-creation-main-dark.webp",
    imageLight: "/assets/features/intelligent-creation-main-light.webp",
  },
  {
    id: "styling",
    title: "Consistent Styling",
    description:
      "Never format manually again. Artifact learns your preferred styles and applies them automatically across documents, presentations, and content. Upload examples or describe what you want—then let AI handle the tedious work of making everything look cohesive and professional.",
    icon: Paintbrush,
    imageDark: "/assets/features/canvas-generation-dark.webp",
    imageLight: "/assets/features/canvas-generation-light.webp",
  },
];

// Category 2: Knowledge & Research
const knowledgeResearchFeatures = [
  {
    id: "research",
    title: "Comprehensive Research",
    description:
      "Ask big questions, get complete answers. Artifact conducts multi-source research that would take you hours in minutes. It reads, compares, synthesizes, and cites—delivering structured reports rather than scattered links. Perfect for market research, competitive analysis, or deep-dive investigations.",
    icon: Search,
    imageDark: "/assets/features/deep-research-dark.webp",
    imageLight: "/assets/features/deep-research-light.webp",
    objectPosition: "object-bottom",
  },
  {
    id: "knowledge-base",
    title: "Living Knowledge Base",
    description:
      "Your information, always accessible. Artifact remembers past conversations, uploaded documents, and learned preferences. Ask about something you discussed months ago. Reference a document you uploaded last week. Your AI assistant builds institutional knowledge that grows more valuable over time.",
    icon: BookOpen,
    imageDark: "/assets/features/knowledge-integration-dark.webp",
    imageLight: "/assets/features/knowledge-integration-light.webp",
  },
];

// Category 3: Document & Data
const documentDataFeatures = [
  {
    id: "file-processing",
    title: "Universal File Processing",
    description:
      "Any format, instant insights. PDFs, Word docs, spreadsheets, images, presentations—upload anything and start asking questions. Artifact extracts text, interprets data, and understands context. Compare documents side by side, pull specific figures, or generate summaries without reading thousands of pages.",
    icon: Layers,
    imageDark: "/assets/features/document-processing-dark.webp",
    imageLight: "/assets/features/document-processing-light.webp",
  },
  {
    id: "visual",
    title: "Visual Interpretation",
    description:
      "See through AI eyes. Charts, diagrams, screenshots, handwriting—Artifact interprets visual information and explains what it sees. Perfect for understanding complex data visualizations, decoding technical diagrams, or extracting information from images that would otherwise require manual transcription.",
    icon: Eye,
    imageDark: "/assets/features/visual-understanding-dark.webp",
    imageLight: "/assets/features/visual-understanding-light.webp",
  },
];

// Category 4: Team Productivity
const teamProductivityFeatures = [
  {
    id: "workspaces",
    title: "Intelligent Workspaces",
    description:
      "Team knowledge that compounds. Create shared spaces where AI assistants, conversation history, and documents benefit everyone. New team members get up to speed through past conversations. Institutional knowledge stays accessible even as people change roles. Collaboration becomes smarter over time.",
    icon: Users,
    imageDark: "/assets/features/collaboration-dark.webp",
    imageLight: "/assets/features/collaboration-light.webp",
  },
  {
    id: "sharing",
    title: "Frictionless Sharing",
    description:
      "Work flows where it needs to go. Share conversations and canvases with teammates for real-time collaboration, send view-only links to stakeholders, or publish finished work publicly. Customizable permissions mean you control exactly who sees what—from draft to final delivery.",
    icon: Share2,
    imageDark: "/assets/features/instant-sharing-dark.webp",
    imageLight: "/assets/features/instant-sharing-light.webp",
  },
];

// Page Hero component
function PageHero({ shouldReduceMotion }) {
  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: shouldReduceMotion ? 0.4 : 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
            Powerful Features for{" "}
            <span className="bg-gradient-to-r from-purple-400 to-violet-500 bg-clip-text text-transparent">
              Every Workflow
            </span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            A unified platform for creation, research, analysis, and
            collaboration—powered by the world&apos;s best AI models.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// Section Header component
function SectionHeader({ title, description, shouldReduceMotion }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: shouldReduceMotion ? 0.4 : 0.6 }}
      viewport={{ once: true }}
      className="mb-10 md:mb-12"
    >
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-3">
        {title}
      </h2>
      <p className="text-muted-foreground text-base md:text-lg max-w-2xl">
        {description}
      </p>
    </motion.div>
  );
}

// Feature Card component with image and detailed description
function FeatureCard({ feature, index, shouldReduceMotion, resolvedTheme }) {
  const Icon = feature.icon;
  const imageSrc =
    resolvedTheme === "dark" ? feature.imageDark : feature.imageLight;
  const objectPosition = feature.objectPosition || "object-top";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{
        duration: shouldReduceMotion ? 0.3 : 0.5,
        delay: index * 0.1,
      }}
      viewport={{ once: true }}
      className="group relative overflow-hidden rounded-xl border border-border/30 bg-card/50 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
    >
      {/* Image */}
      <div className="aspect-[16/10] overflow-hidden bg-muted">
        <img
          src={imageSrc}
          alt={feature.title}
          className={`w-full h-full object-cover ${objectPosition} transition-transform duration-500 group-hover:scale-105`}
          loading="lazy"
          decoding="async"
        />
      </div>

      {/* Content */}
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-500/10">
            <Icon className="h-5 w-5 text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            {feature.title}
          </h3>
        </div>
        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
          {feature.description}
        </p>
      </div>
    </motion.div>
  );
}

// Feature Section component
function FeatureSection({
  title,
  description,
  features,
  shouldReduceMotion,
  resolvedTheme,
}) {
  return (
    <section className="py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <SectionHeader
          title={title}
          description={description}
          shouldReduceMotion={shouldReduceMotion}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.id}
              feature={feature}
              index={index}
              shouldReduceMotion={shouldReduceMotion}
              resolvedTheme={resolvedTheme}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// Try Artifact CTA component
function TryArtifactCTA({ shouldReduceMotion }) {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/auth/login");
  };

  const handleViewPricing = () => {
    navigate("/pricing");
  };

  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: shouldReduceMotion ? 0.4 : 0.6 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-900/90 via-violet-800/80 to-purple-900/90 p-8 md:p-12 lg:p-16"
        >
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[url('/assets/features/bg-compress.webp')] bg-cover bg-center opacity-10" />

          <div className="relative text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Try Artifact Now
            </h2>
            <p className="text-white/80 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              Experience the future of AI-powered collaboration. Start building
              faster with intelligent AI assistance across multiple models.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                onClick={handleGetStarted}
                className="bg-white text-purple-900 hover:bg-purple-50 hover:text-purple-800 border-2 border-white px-8 py-6 text-base font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105"
              >
                Get Started Free
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleViewPricing}
                className="border-2 border-white/50 text-white hover:bg-white/20 hover:border-white px-8 py-6 text-base font-semibold rounded-xl transition-all duration-300 bg-transparent"
              >
                View Pricing
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function FeaturesPage() {
  const { shouldReduceMotion } = useMobileOptimized();
  const { resolvedTheme } = useTheme();

  return (
    <>
      <Helmet>
        <title>Features | Artifact - AI Assistant Platform</title>
        <meta
          name="description"
          content="Explore Artifact's powerful features: intelligent canvas creation, comprehensive research, document processing, and team collaboration—all powered by the world's best AI models."
        />
        <meta
          name="keywords"
          content="AI features, canvas creation, AI research, document processing, team collaboration, knowledge management"
        />
        <link rel="canonical" href="https://artifact.chat/features" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Features | Artifact - AI Assistant Platform"
        />
        <meta
          property="og:description"
          content="Explore Artifact's powerful features: intelligent canvas creation, comprehensive research, document processing, and team collaboration."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://artifact.chat/features" />
        <meta
          property="og:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Features | Artifact - AI Assistant Platform"
        />
        <meta
          name="twitter:description"
          content="Explore Artifact's powerful features: intelligent canvas creation, comprehensive research, document processing, and team collaboration."
        />
      </Helmet>

      <div className="min-h-screen bg-transparent">
        {/* Page Hero */}
        <PageHero shouldReduceMotion={shouldReduceMotion} />

        {/* Content Creation */}
        <FeatureSection
          title="Content Creation"
          description="From blank page to finished product"
          features={contentCreationFeatures}
          shouldReduceMotion={shouldReduceMotion}
          resolvedTheme={resolvedTheme}
        />

        {/* Knowledge & Research */}
        <FeatureSection
          title="Knowledge & Research"
          description="Find answers, not just links"
          features={knowledgeResearchFeatures}
          shouldReduceMotion={shouldReduceMotion}
          resolvedTheme={resolvedTheme}
        />

        {/* Document & Data */}
        <FeatureSection
          title="Document & Data"
          description="Make sense of any file"
          features={documentDataFeatures}
          shouldReduceMotion={shouldReduceMotion}
          resolvedTheme={resolvedTheme}
        />

        {/* Team Productivity */}
        <FeatureSection
          title="Team Productivity"
          description="Amplify what your team can do"
          features={teamProductivityFeatures}
          shouldReduceMotion={shouldReduceMotion}
          resolvedTheme={resolvedTheme}
        />

        {/* Try Artifact CTA */}
        <TryArtifactCTA shouldReduceMotion={shouldReduceMotion} />
      </div>
    </>
  );
}
